import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import get from 'lodash/get';
import {
    FileText,
    Wrench,
    Users,
    Package,
    MessageSquare,
    Mail,
    ShieldCheck,
    Truck,
    Box,
    ChevronLeft,
    ChevronRight,
    Beaker,
    Layers,
    Contact,
    History,
    Sparkles,
    ClipboardList,
} from 'lucide-react';

// Components
import Admin from './asset-management';
import EquipmentSubCategory from './EquipmentSubCategory';
import DocControlMangement from '../../components/Calibration/DocControlMangement';
import AdminEmployees from '../../components/AdminEmployees';
import Spares from './spares-managment';
import ToolboxAdmin from '../../components/ToolboxAdmin';
import AdminReportingEmailPage from '../../components/AdminReportingEmailPage';
import UserManagementAGGrid from '../userManagement/UserManagementAGGrid';
import ShippingManagement from '../../components/ShippingManagement/ShippingManagement';
import { ConsumableMasterAdmin, MaterialMasterAdmin } from '../../components/admin/ConsumableMaterialAdmin';
import RolesAdmin from '../../components/admin/RolesAdmin';
import AdminAuditTrailPage from './AdminAuditTrailPage';
import AdminAiSettingsPage from './AdminAiSettingsPage';
import AdminAskEvansAuditPage from './AdminAskEvansAuditPage';
import { isSuperAdminRole, normalizeCognitoGroups } from '../../utils/userGroupUtils';

// Styles
import '../technical/HeatProfilePage.css';
import '../calibration/CalibrationPage.css';
import './AdminModernPage.css';
import '../../layout/ProjectTabsStrip.css';

const TAB_ITEMS = [
    { id: 'asset-management', label: 'Asset Management', icon: <Box size={18} />, path: '/admin/asset-management' },
    { id: 'shipping-management', label: 'Shipping Management', icon: <Truck size={18} />, path: '/admin/shipping-management' },
    { id: 'user-management', label: 'User Management', icon: <ShieldCheck size={18} />, path: '/admin/user-management' },
    { id: 'reporting-email', label: 'Reporting Email', icon: <Mail size={18} />, path: '/admin/reporting-email' },
    { id: 'communication', label: 'Communication', icon: <MessageSquare size={18} />, path: '/admin/communications' },
    { id: 'spares', label: 'Spares', icon: <Package size={18} />, path: '/admin/spares' },
    { id: 'employees', label: 'Employees', icon: <Users size={18} />, path: '/admin/employees' },
    { id: 'roles', label: 'Roles', icon: <Contact size={18} />, path: '/admin/roles' },
    { id: 'documentation', label: 'Documentation', icon: <FileText size={18} />, path: '/admin/documentation' },
    { id: 'consumable', label: 'Consumable', icon: <Beaker size={18} />, path: '/admin/consumable' },
    { id: 'material', label: 'Material', icon: <Layers size={18} />, path: '/admin/material' },
    { id: 'calibration', label: 'Calibration & Control', icon: <Wrench size={18} />, path: '/admin/calibration-control-users' },
    { id: 'audit-trail', label: 'Audit Trail', icon: <History size={18} />, path: '/admin/audit-trail', superAdminOnly: true },
    { id: 'ai-settings', label: 'AI Configuration', icon: <Sparkles size={18} />, path: '/admin/ai', adminOnly: true },
    { id: 'ai-usage', label: 'AI Usage', icon: <ClipboardList size={18} />, path: '/admin/ai-usage', adminOnly: true },
];

function readUserGroups(userAccessList) {
    if (Array.isArray(userAccessList) && userAccessList.length > 0) {
        return normalizeCognitoGroups(userAccessList);
    }
    try {
        return normalizeCognitoGroups(JSON.parse(localStorage.getItem('UserGroup') || '[]'));
    } catch {
        return [];
    }
}

function filterVisibleTabItems(groups) {
    const isSuperAdmin = isSuperAdminRole(groups);
    const isAdminUser =
        groups.includes('admin') || groups.includes('super-admin');
    return TAB_ITEMS.filter((t) => {
        if (t.superAdminOnly && !isSuperAdmin) return false;
        if (t.adminOnly && !isAdminUser) return false;
        return true;
    });
}

function getTabIdForPathname(pathname, visibleItems) {
    const items = visibleItems || TAB_ITEMS;
    const item = items.find((t) => t.path === pathname);
    return item ? item.id : (items[0]?.id || 'documentation');
}

const Tabs = ({ items, activeTabId, onTabClick }) => {
    const tabsViewportRef = useRef(null);
    const [tabScrollState, setTabScrollState] = useState({
        hasOverflow: false,
        canPrev: false,
        canNext: false,
    });

    const updateTabScrollEdges = useCallback(() => {
        const el = tabsViewportRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const hasOverflow = scrollWidth > clientWidth + 2;
        setTabScrollState({
            hasOverflow,
            canPrev: hasOverflow && scrollLeft > 2,
            canNext: hasOverflow && scrollLeft < scrollWidth - clientWidth - 2,
        });
    }, []);

    useEffect(() => {
        if (!tabsViewportRef.current || !activeTabId) return;
        const activeEl = tabsViewportRef.current.querySelector('.calibration-tab-item.active');
        if (activeEl) {
            activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
    }, [activeTabId]);

    useEffect(() => {
        const id = requestAnimationFrame(() => {
            updateTabScrollEdges();
        });
        return () => cancelAnimationFrame(id);
    }, [items, updateTabScrollEdges]);

    useEffect(() => {
        const el = tabsViewportRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => {
            updateTabScrollEdges();
        });
        ro.observe(el);
        return () => {
            ro.disconnect();
        };
    }, [updateTabScrollEdges, items.length]);

    const scrollTabsBy = (direction) => {
        const el = tabsViewportRef.current;
        if (!el) return;
        const delta = Math.min(280, Math.max(120, Math.floor(el.clientWidth * 0.75)));
        el.scrollBy({ left: direction * delta, behavior: 'smooth' });
    };

    const { hasOverflow, canPrev, canNext } = tabScrollState;

    return (
        <div
            className={`calibration-tabs-container modern-admin-tabs project-tabs-strip${!hasOverflow ? ' project-tabs-strip--no-overflow' : ''}`}
        >
            <div className="project-tabs-strip__row">
                <button
                    type="button"
                    className="project-tabs-strip__btn project-tabs-strip__btn--prev"
                    onClick={() => scrollTabsBy(-1)}
                    disabled={!hasOverflow || !canPrev}
                    aria-label="Scroll admin tabs left"
                >
                    <ChevronLeft size={22} strokeWidth={2} aria-hidden />
                </button>
                <div
                    className="project-tabs-strip__viewport"
                    ref={tabsViewportRef}
                    onScroll={updateTabScrollEdges}
                    role="presentation"
                >
                    <div className="calibration-tabs">
                        {items.map((item) => (
                            <div
                                key={item.id}
                                className={`calibration-tab-item ${activeTabId === item.id ? 'active' : ''}`}
                                onClick={() => onTabClick(item)}
                            >
                                {item.icon}
                                {item.label}
                            </div>
                        ))}
                    </div>
                </div>
                <button
                    type="button"
                    className="project-tabs-strip__btn project-tabs-strip__btn--next"
                    onClick={() => scrollTabsBy(1)}
                    disabled={!hasOverflow || !canNext}
                    aria-label="Scroll admin tabs right"
                >
                    <ChevronRight size={22} strokeWidth={2} aria-hidden />
                </button>
            </div>
        </div>
    );
};

const AdminModernPage = ({ userAccessList }) => {
    const history = useHistory();
    const location = useLocation();
    const groups = useMemo(() => readUserGroups(userAccessList), [userAccessList]);
    const isSuperAdmin = isSuperAdminRole(groups);
    const isAdminUser =
        groups.includes('admin') || groups.includes('super-admin');

    const visibleTabItems = useMemo(
        () => filterVisibleTabItems(groups),
        [groups]
    );

    // Must match the URL on first paint; a wrong default (e.g. "documentation") mounts the wrong
    // tab briefly and duplicates all admin XHRs (product-hierarchies, pih-equipment-types, etc.).
    const [activeTab, setActiveTab] = useState(() =>
        getTabIdForPathname(location.pathname, filterVisibleTabItems(readUserGroups(null)))
    );

    // Sync active tab with URL (back/forward, external navigation)
    useEffect(() => {
        setActiveTab(getTabIdForPathname(location.pathname, visibleTabItems));
    }, [location.pathname, visibleTabItems]);

    useEffect(() => {
        if (location.pathname === '/admin/audit-trail' && !isSuperAdmin) {
            history.replace('/admin/asset-management');
            return;
        }
        if (
            (location.pathname === '/admin/ai' || location.pathname === '/admin/ai-usage') &&
            !isAdminUser
        ) {
            history.replace('/admin/asset-management');
        }
    }, [location.pathname, isSuperAdmin, isAdminUser, history]);

    const handleTabClick = (item) => {
        setActiveTab(item.id);
        history.push(item.path);
    };

    return (
        <div className="heat-profile-page admin-modern-page">
            <Tabs
                items={visibleTabItems}
                activeTabId={activeTab}
                onTabClick={handleTabClick}
            />

            <div className="modern-admin-content">
                {activeTab === 'asset-management' && <Admin isModernPage={true} />}
                {activeTab === 'documentation' && <EquipmentSubCategory />}
                {activeTab === 'calibration' && <DocControlMangement className="mt-0" />}
                {activeTab === 'employees' && <AdminEmployees />}
                {activeTab === 'roles' && <RolesAdmin />}
                {activeTab === 'spares' && <Spares />}
                {activeTab === 'communication' && <ToolboxAdmin />}
                {activeTab === 'reporting-email' && <AdminReportingEmailPage />}
                {activeTab === 'consumable' && <ConsumableMasterAdmin />}
                {activeTab === 'material' && <MaterialMasterAdmin />}
                {activeTab === 'user-management' && <UserManagementAGGrid />}
                {activeTab === 'shipping-management' && <ShippingManagement isModernPage={true} />}
                {activeTab === 'audit-trail' && isSuperAdmin && <AdminAuditTrailPage />}
                {activeTab === 'ai-settings' && isAdminUser && <AdminAiSettingsPage />}
                {activeTab === 'ai-usage' && isAdminUser && <AdminAskEvansAuditPage />}
            </div>
        </div>
    );
};

const mapStateToProps = ({ equip }) => ({
    userAccessList: get(equip, 'userAccessList', []),
});

export default connect(mapStateToProps)(AdminModernPage);
